import { Observable, map } from 'rxjs';

import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Inject, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatStepper, MatStepperModule } from '@angular/material/stepper';

import { PermissionUtils } from '@mitsubishi/core';
import {
  ProjectSelectionsFacade,
  SelectionService,
  StatisticData,
  StatisticDataArray,
  StatisticOption,
} from '@mitsubishi/project/data-access';
import { BaseUiModule } from '@mitsubishi/shared/ui';

import { StatisticDataItemComponent } from '../statistics-data-item/statistic-data-item.component';

@Component({
  selector: 'apm-statistic-data-dialog',
  templateUrl: './statistic-data-dialog.component.html',
  styleUrls: ['./statistic-data-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, BaseUiModule, MatStepperModule, StatisticDataItemComponent],
})
export class StatisticDataDialogComponent {
  @ViewChild(MatStepper) stepper: MatStepper | null = null;

  hasDatasheetMoreDetails = PermissionUtils.hasDatasheetMoreDetails();

  selectedStatistics: Record<string, string> = {};

  statisticList$: Observable<StatisticDataArray[]> = this.quoteService.getStatisticData().pipe(
    map(data => {
      const statisticsDataArray = Object.values(data).sort((a, b) => a.displayOrder - b.displayOrder);

      return statisticsDataArray.map((stData, index) => {
        return {
          ...stData,
          previousGroupCode: statisticsDataArray[index - 1]?.groupCode ?? null,
          statistics: stData.statistics
            ? Object.values(stData.statistics).sort((a, b) => a.displayOrder - b.displayOrder)
            : [],
        };
      });
    })
  );

  constructor(
    @Inject(MAT_DIALOG_DATA) public statisticsData: { [key: string]: StatisticData } | null,
    public dialogRef: MatDialogRef<StatisticDataDialogComponent>,
    private quoteService: SelectionService,
    private quoteFacade: ProjectSelectionsFacade
  ) {
    if (this.statisticsData) {
      this.selectedStatistics = Object.values(this.statisticsData).reduce(
        (acc, curr) => {
          if (curr.statistics) {
            acc[curr.groupCode] = Object.keys(curr.statistics)[0];
          }

          return acc;
        },
        {} as Record<string, string>
      );
    }
  }

  goPrev() {
    this.stepper?.previous();
  }

  goNext() {
    this.stepper?.next();
  }

  onStatisticSelected(statistic: StatisticOption, groupCode: string) {
    this.selectedStatistics[groupCode] = statistic.statisticCode;
  }

  confirm() {
    const selectionId = this.quoteFacade.activeSelection?.details.uuid;
    selectionId && this.quoteFacade.setStatisticData(selectionId, this.getStatisticsDataPayload());
    this.dialogRef.close(true);
  }

  skipAnswers(statisticGroup: StatisticDataArray[]) {
    const selectionId = this.quoteFacade.activeSelection?.details.uuid;
    if (!selectionId) {
      return;
    }

    this.selectedStatistics = statisticGroup.reduce(
      (acc, curr) => {
        const previousGroupCode = curr.previousGroupCode;
        const groupStatistics = curr.statistics ?? [];
        if (groupStatistics.length === 0) {
          return acc;
        }

        if (!previousGroupCode) {
          acc[curr.groupCode] = groupStatistics[0].statisticCode;
          return acc;
        }

        const filteredStatistics = groupStatistics.filter(statistic => {
          return !statistic.dependsOn || statistic.dependsOn == acc[previousGroupCode];
        });
        if (filteredStatistics.length === 0) {
          return acc;
        }

        acc[curr.groupCode] = filteredStatistics[0].statisticCode;

        return acc;
      },
      {} as Record<string, string>
    );

    this.quoteFacade.setStatisticData(selectionId, this.getStatisticsDataPayload());
    this.dialogRef.close(true);
  }

  private getStatisticsDataPayload(): StatisticDataArray[] {
    return Object.keys(this.selectedStatistics).reduce((acc, curr) => {
      acc.push({
        groupCode: curr,
        statisticCode: this.selectedStatistics[curr],
      });

      return acc;
    }, [] as StatisticDataArray[]);
  }
}
